import { useMemo } from 'react';
import useGetFloorplan from '@store/hooks/useGetFloorplan';
import { transformFPData } from '../zoneViewer/zoneMapDataHelper';
import { SwitchButtonValue } from '../types/zoneMapType';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { buildFixtureOrderingMap } from '../pickPath/buildFixtureOrderingMap';

interface UseFloorplanDataInput {
  store: string;
  timeStamp: number;
  copyToNewTimestamp: number;
  switchButtonValue: string | undefined;
  selectedDepartment: string;
}

export function useFloorplanData({
  store,
  timeStamp,
  copyToNewTimestamp,
  switchButtonValue,
  selectedDepartment,
}: UseFloorplanDataInput) {
  const config = useProgramConfig();
  const supportsDepartmentFilter = config.zoneMapBehavior.supportsDepartmentFilter;

  const {
    floorplanList,
    isLoading: isGetFloorplanLoading,
    isSuccess: isGetFloorplanSuccess,
  } = useGetFloorplan({
    storeId: store,
    date: switchButtonValue !== SwitchButtonValue.CopyToNewMap ? timeStamp : copyToNewTimestamp,
  });

  const fixtures = useMemo(() => (floorplanList.length > 0 ? (floorplanList[0].fixtures ?? []) : []), [floorplanList]);
  const fpData = useMemo(() => transformFPData(fixtures), [fixtures]);

  const availableDepartments = useMemo(() => {
    const depts = new Set<string>();
    fixtures.forEach((f) => {
      if (f.department) depts.add(f.department);
    });
    return Array.from(depts).sort();
  }, [fixtures]);

  const fixtureOrderingMap = useMemo(() => {
    // Department-filtering programs define product flow per department, so a
    // store-wide flow across all departments is meaningless.
    if (supportsDepartmentFilter && !selectedDepartment) {
      return new Map<string, number>();
    }
    const sourceFixtures =
      supportsDepartmentFilter && selectedDepartment
        ? fixtures.filter((f) => f.department === selectedDepartment)
        : fixtures;
    return buildFixtureOrderingMap(sourceFixtures);
  }, [fixtures, supportsDepartmentFilter, selectedDepartment]);

  const filteredFpData = useMemo(() => {
    if (!supportsDepartmentFilter) {
      return fpData; // GFC: no department filtering — show all fixtures
    }
    if (selectedDepartment) {
      return transformFPData(fixtures.filter((f) => f.department === selectedDepartment));
    }
    return fpData;
  }, [fixtures, selectedDepartment, fpData, supportsDepartmentFilter]);

  const agileFixtures = useMemo(() => {
    if (!supportsDepartmentFilter) {
      return fixtures; // GFC: no department filtering — return all fixtures
    }
    if (selectedDepartment) {
      return fixtures.filter((fixture) => fixture.department === selectedDepartment);
    }
    return fixtures;
  }, [fixtures, selectedDepartment, supportsDepartmentFilter]);

  return {
    fixtures,
    fpData,
    filteredFpData,
    agileFixtures,
    availableDepartments,
    fixtureOrderingMap,
    floorplanList,
    isGetFloorplanLoading,
    isGetFloorplanSuccess,
  };
}
